import { useMemo } from "react";
import type { MouseEvent } from "react";
import type {
  ColumnDef,
  PaginationState,
  RowData,
} from "@tanstack/react-table";
import {
  createColumnHelper,
  flexRender,
  useTable,
} from "@tanstack/react-table";
import { ArrowDown, ArrowUp, ArrowUpDown } from "lucide-react";
import { cn } from "../../lib/cn";
import { EmptyStateIllustration } from "../EmptyState";
import { Skeleton } from "../Skeleton";
import { DataTablePagination } from "./DataTablePagination";
import type {
  DataTableColumnDef,
  DataTableProps,
  DefaultTableFeatures,
} from "./types";
import { defaultTableFeatures } from "./types";

const INTERACTIVE_SELECTOR =
  'a, button, input, select, textarea, label, [role="button"], [role="checkbox"], [role="switch"], [role="menuitem"], [data-row-click-ignore]';

// A row click opens the row, but not when the click belongs to something else:
// a control inside a cell (the actions menu trigger, a switch), a portalled
// menu item -- React bubbles portal events through the row even though the
// DOM node lives elsewhere -- or the end of a text selection.
function isRowActivation(event: MouseEvent<HTMLTableRowElement>) {
  const target = event.target as Element;
  if (!event.currentTarget.contains(target)) return false;
  const interactive = target.closest(INTERACTIVE_SELECTOR);
  if (interactive && event.currentTarget.contains(interactive)) return false;
  return !window.getSelection()?.toString();
}

// Column sizing: the first column is the row's identity (name, code, ...) and
// takes all leftover width, with a floor so it can't be squeezed on narrow
// screens; every other column shrinks to fit its content on one line. The
// actions column only ever holds one 32px icon-button trigger (see
// DataTableActionMenu), so it's fixed.
function colStyle(columnId: string, index: number) {
  if (columnId === "actions") return { width: "3.5rem" };
  return index === 0 ? undefined : { width: "1%" };
}

function cellSizeClassName(index: number) {
  return index === 0 ? "min-w-56" : "whitespace-nowrap";
}

export function createDataTableColumnHelper<TData extends RowData>() {
  return createColumnHelper<DefaultTableFeatures, TData>();
}

export function DataTable<TData extends RowData, TValue = unknown>({
  data,
  columns,
  paginationMeta,
  sorting,
  onSortingChange,
  page,
  pageIndex,
  pageSize,
  pageSizeOptions,
  onPaginationChange,
  onPageSizeChange,
  isLoading = false,
  isError = false,
  onRowClick,
  labels,
  className,
  containerClassName,
  skeletonRowCount,
}: DataTableProps<TData, TValue>) {
  const resolvedPageIndex =
    pageIndex ??
    (page !== undefined
      ? page
      : paginationMeta
        ? Math.max(0, paginationMeta.currentPage - 1)
        : 0);

  const resolvedPageSize = pageSize ?? 10;

  const paginationState = useMemo<PaginationState>(
    () => ({
      pageIndex: resolvedPageIndex,
      pageSize: resolvedPageSize,
    }),
    [resolvedPageIndex, resolvedPageSize],
  );

  const normalizedColumns = columns as unknown as ReadonlyArray<
    ColumnDef<DefaultTableFeatures, TData, unknown>
  >;

  // The table builds its rows from `data` only, and each row keeps what the
  // column accessors returned. Screens rebuild their columns when what the
  // accessors compute changes -- a language switch, say -- so new columns
  // get new rows, or the cells would keep the old values under new headers.
  // `columns` is listed for exactly that, though the copy doesn't read it.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const rows = useMemo(() => [...data], [data, columns]);

  const tableOptions = {
    features: defaultTableFeatures,
    data: rows,
    columns: normalizedColumns,
    manualPagination: true,
    manualSorting: true,
    manualFiltering: true,
    // List screens persist their sort in the URL and every click should be a
    // useful direction change.  Do not cycle back to an unsorted state: that
    // would silently replace an explicitly chosen API sort with its default.
    enableSortingRemoval: false,
    enableMultiSort: false,
    state: {
      pagination: paginationState,
      sorting: sorting ?? [],
    },
    ...(onPaginationChange !== undefined ? { onPaginationChange } : {}),
    ...(onSortingChange !== undefined ? { onSortingChange } : {}),
    ...(paginationMeta?.totalPages !== undefined
      ? { pageCount: paginationMeta.totalPages }
      : {}),
    ...(paginationMeta?.totalCount !== undefined
      ? { rowCount: paginationMeta.totalCount }
      : {}),
  };

  const table = useTable(tableOptions);

  const headerGroups = table.getHeaderGroups();
  const columnCount = Math.max(columns.length, 1);
  const effectiveSkeletonRows =
    skeletonRowCount ?? Math.min(resolvedPageSize, 5);
  const isEmpty = table.getRowModel().rows.length === 0;
  // Keyed so loading/error/empty/data swaps remount the body and crossfade
  // instead of popping; paging within the same state keeps this key stable.
  const bodyState = isLoading
    ? "loading"
    : isError
      ? "error"
      : isEmpty
        ? "empty"
        : "data";
  // Nothing to sort or page through: the headers drop their sort control and
  // the pagination footer goes away. Loading keeps both so they don't flicker.
  const hasNoRows = !isLoading && (isError || isEmpty);

  return (
    <div
      className={cn(
        "w-full min-w-0 overflow-hidden rounded-md border-y border-table-cell-bottom-border bg-container-default-background",
        containerClassName,
      )}
    >
      {/* Scrolls both ways: a caller that bounds the table's height (a flex
          column containerClassName) gets rows that scroll under the sticky
          header, with the pagination kept in view. */}
      <div className="min-h-0 w-full min-w-0 flex-1 overflow-auto">
        <table
          aria-busy={isLoading ? "true" : undefined}
          className={cn(
            "w-full border-collapse text-start text-body-m-r",
            className,
          )}
        >
          {isLoading ? (
            <caption className="sr-only">{labels.loading}</caption>
          ) : null}
          <colgroup>
            {(headerGroups[0]?.headers ?? []).map((header, index) => (
              // <col> width is the only reliable way to pin a column's
              // width under table-auto layout -- a width class on the th/td
              // themselves is just a hint the browser can still override
              // when redistributing the row's leftover width. A 1% width
              // shrinks a column to its content, so the one without a width
              // gets the rest.
              <col key={header.id} style={colStyle(header.column.id, index)} />
            ))}
          </colgroup>
          <thead className="sticky top-0 z-10 bg-table-column-header">
            {headerGroups.map((headerGroup) => (
              <tr
                key={headerGroup.id}
                className="h-9 border-b border-table-cell-bottom-border"
              >
                {headerGroup.headers.map((header, index) => {
                  const canSort = header.column.getCanSort() && !hasNoRows;
                  const isSorted = !hasNoRows && header.column.getIsSorted();

                  return (
                    <th
                      key={header.id}
                      colSpan={header.colSpan}
                      aria-sort={
                        isSorted === "asc"
                          ? "ascending"
                          : isSorted === "desc"
                            ? "descending"
                            : undefined
                      }
                      className={cn(
                        "h-9 px-3 text-start align-middle text-body-caption-bold text-text-placeholder select-none",
                        cellSizeClassName(index),
                      )}
                    >
                      {header.isPlaceholder ? null : canSort ? (
                        <button
                          type="button"
                          onClick={header.column.getToggleSortingHandler()}
                          aria-label={
                            isSorted === "asc"
                              ? labels.sortDescending
                              : labels.sortAscending
                          }
                          className="group flex w-full items-center justify-between gap-1.5 rounded-sm text-start transition-colors hover:text-text focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary"
                        >
                          {/* Not truncated: a fit-to-content column would
                              shrink its header down to the ellipsis. */}
                          <span className="whitespace-nowrap">
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )}
                          </span>
                          <span className="inline-flex size-4 shrink-0 items-center justify-center">
                            {isSorted === "asc" ? (
                              <ArrowUp className="size-3.5 text-text" />
                            ) : isSorted === "desc" ? (
                              <ArrowDown className="size-3.5 text-text" />
                            ) : (
                              <ArrowUpDown className="size-3.5 opacity-40 transition-opacity duration-140 ease-emphasized group-hover:opacity-100" />
                            )}
                          </span>
                        </button>
                      ) : (
                        <span className="inline-flex items-center">
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                        </span>
                      )}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody key={bodyState} className="animate-in fade-in-0 duration-200">
            {isLoading ? (
              Array.from({ length: effectiveSkeletonRows }, (_, rowIndex) => (
                <tr
                  key={`skeleton-row-${rowIndex}`}
                  className="h-12 border-b border-table-cell-bottom-border"
                >
                  {columns.map((column, colIndex) => (
                    <td
                      key={`skeleton-cell-${rowIndex}-${colIndex}`}
                      className={cn(
                        "px-3 py-2 align-middle",
                        cellSizeClassName(colIndex),
                      )}
                    >
                      <Skeleton
                        className={
                          column.id === "actions"
                            ? "size-4"
                            : "h-4 w-full max-w-[80%]"
                        }
                      />
                    </td>
                  ))}
                </tr>
              ))
            ) : isError ? (
              <tr>
                <td
                  colSpan={columnCount}
                  className="px-4 py-12 text-center text-body-m-r text-fail-text"
                >
                  {labels.error}
                </td>
              </tr>
            ) : isEmpty ? (
              <tr>
                <td colSpan={columnCount} className="px-4 py-12">
                  <div className="flex flex-col items-center gap-3">
                    <EmptyStateIllustration />
                    <p className="text-center text-body-m-r text-text-placeholder">
                      {labels.empty}
                    </p>
                  </div>
                </td>
              </tr>
            ) : (
              table.getRowModel().rows.map((row) => (
                <tr
                  key={row.id}
                  tabIndex={onRowClick ? 0 : undefined}
                  onClick={
                    onRowClick
                      ? (event) => {
                          if (isRowActivation(event)) onRowClick(row.original);
                        }
                      : undefined
                  }
                  onKeyDown={
                    onRowClick
                      ? (event) => {
                          if (
                            event.key === "Enter" &&
                            event.target === event.currentTarget
                          ) {
                            onRowClick(row.original);
                          }
                        }
                      : undefined
                  }
                  className={cn(
                    // Opacity-only and unstaggered so a full page of rows stays cheap to mount.
                    "h-12 border-b border-table-cell-bottom-border transition-colors hover:bg-container-hover animate-in fade-in-0 duration-200",
                    onRowClick &&
                      "cursor-pointer focus-visible:bg-container-hover focus-visible:outline-none",
                  )}
                >
                  {row.getVisibleCells().map((cell, index) => (
                    <td
                      key={cell.id}
                      className={cn(
                        "px-3 py-2 text-start align-middle text-body-m-r text-text",
                        cellSizeClassName(index),
                      )}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {paginationMeta && !hasNoRows ? (
        <DataTablePagination
          meta={paginationMeta}
          pageSize={resolvedPageSize}
          displayedCount={data.length}
          onPageChange={(nextPageIndex) => table.setPageIndex(nextPageIndex)}
          onPreviousPage={() => table.previousPage()}
          onNextPage={() => table.nextPage()}
          onPageSizeChange={(size) => {
            table.setPageSize(size);
            onPageSizeChange?.(size);
          }}
          pageSizeOptions={pageSizeOptions}
          rowsPerPageLabel={labels.rowsPerPageLabel}
          labels={labels}
        />
      ) : null}
    </div>
  );
}

export type { DataTableColumnDef };
